import { Box, Chip, Typography } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import CancelIcon from "@material-ui/icons/Cancel";
import { useEffect, useState } from "react";
import api from "../../../api";
import DetailSection from "../../../components/DetailSection";

function StatusChip({ ok, label }) {
  return (
    <Chip
      size="small"
      icon={ok ? <CheckCircleIcon /> : <CancelIcon />}
      label={label}
      style={{
        backgroundColor: ok ? "#e6f4ea" : "#fce8e6",
        color: ok ? "#137333" : "#a50e0e"
      }}
    />
  );
}

function BillingConfigurationCard({ configuration }) {
  const ach = configuration.achAccount;
  const payout = configuration.payoutAccount;
  const externalAccount = payout?.externalAccount;
  const partnerAttachments = configuration.attachments?.filter(
    (a) => a.ownerType === "Boom::Partner"
  ).length;
  const propertyAttachments = configuration.attachments?.filter(
    (a) => a.ownerType === "Boom::Property"
  ).length;

  return (
    <Box
      mb={2}
      p={2}
      style={{ border: "1px solid #e0e0e0", borderRadius: 4 }}
    >
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Typography variant="subtitle1">
          <strong>{configuration.name}</strong>
        </Typography>
        <Typography variant="caption" color="textSecondary">
          ID: {configuration.id}
        </Typography>
      </Box>

      {configuration.billingNotificationEmail && (
        <Typography variant="body2" color="textSecondary">
          Notifications: {configuration.billingNotificationEmail}
        </Typography>
      )}

      <Typography variant="caption" color="textSecondary">
        Attached to {partnerAttachments || 0} partner(s),{" "}
        {propertyAttachments || 0} property(ies)
      </Typography>

      <Box mt={1.5}>
        <Typography variant="body2">
          <strong>ACH (invoice) account</strong>
        </Typography>
        {ach?.present ? (
          <Typography variant="body2" color="textSecondary">
            Stripe customer:{" "}
            <code>{ach.stripeCustomerId || "—"}</code>
          </Typography>
        ) : (
          <Typography variant="body2" color="textSecondary">
            Not configured
          </Typography>
        )}
      </Box>

      <Box mt={1.5}>
        <Typography variant="body2">
          <strong>Payout account</strong>
        </Typography>
        {payout?.present ? (
          <>
            <Typography variant="body2" color="textSecondary">
              Stripe account:{" "}
              <code>{payout.stripeAccountId || "—"}</code>{" "}
              {payout.status && `(${payout.status})`}
            </Typography>
            <Box mt={0.5} display="flex" gridGap={8} flexWrap="wrap">
              <StatusChip
                ok={payout.chargesEnabled}
                label={`Charges ${payout.chargesEnabled ? "enabled" : "disabled"}`}
              />
              <StatusChip
                ok={payout.payoutsEnabled}
                label={`Payouts ${payout.payoutsEnabled ? "enabled" : "disabled"}`}
              />
            </Box>
            {externalAccount && (
              <Typography
                variant="body2"
                color="textSecondary"
                style={{ marginTop: 4 }}
              >
                Bank: {externalAccount.bankName || "—"} ••••
                {externalAccount.last4 || "----"}{" "}
                {externalAccount.status && `(${externalAccount.status})`}
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="body2" color="textSecondary">
            Not configured
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export function BillingSlice({ partner }) {
  const enabled = partner?.features?.includes("stripe_managed_billing");
  const [configurations, setConfigurations] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!enabled || !partner?.id) return;
    let cancelled = false;
    api
      .getPartnerBillingConfigurations({ id: partner.id })
      .then(api.pickData)
      .then((data) => {
        if (!cancelled) setConfigurations(data || []);
      })
      .catch((err) => {
        if (!cancelled)
          setError(
            err?.response?.data?.error?.message ||
              "Failed to load billing configurations"
          );
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, partner?.id]);

  if (!enabled) return null;

  return (
    <DetailSection title="Stripe Billing" id="billing">
      {error && (
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      )}
      {!error && configurations === null && (
        <Typography variant="body2" color="textSecondary">
          Loading…
        </Typography>
      )}
      {!error && configurations?.length === 0 && (
        <Typography variant="body2" color="textSecondary">
          No billing configurations attached to this partner.
        </Typography>
      )}
      {configurations?.map((configuration) => (
        <BillingConfigurationCard
          key={configuration.id}
          configuration={configuration}
        />
      ))}
    </DetailSection>
  );
}
